import { distinct, fullUri } from "@eusoft/webapp-framework/helpers/Utils";
import { userInteraction } from "@eusoft/webapp-framework/services/UserInteraction";
import { forModel } from "@eusoft/webapp-jsx";
import { Image, Content, contentInfo, MaterialIcon, ObjectEditor, Popup, required, staticItemsSource, type IAction, formatText, Action, Message, TimeSpan } from "@eusoft/webapp-ui";
import { PermissionEditor, type IPermissionsData } from "../../components/PermissionEditor";
import { Authorize } from "../../services/Authorize";
import { config } from "../../services/Configuration";
import { executeAsync } from "../../services/DataService";
import { isNativeApp, nativeApp } from "../../services/NativePayment";
import { apiClient } from "../../services/PmApiClient";
import "./DelegateDevicePage.scss";
import { registerComponent } from "@eusoft/webapp-core/Component";

interface IDelegateData {
    name: string;
    expireTime: TimeSpan;
    permissions: IPermissionsData;
}

export class DelegateDevicePage extends Content {
    constructor() {

        super();

        const completeActions: IAction[] = [{
            name: "delegate",
            text: "delegate",
            executeAsync: () => this.delegateAsync(),
        }];

        this.init(DelegateDevicePage, {
            title: "delegate-device",
            style: ["panel"],
            actions: completeActions,
            body: forModel(this, m => <>
                <ObjectEditor<IDelegateData>
                    name="data"
                    validationMode="manual"
                    style={["vertical"]}
                    inputField={{ style: ["filled"] }}
                    ref={m.editor}
                    commitMode="manual-inplace"
                    builder={bld => <>

                        {bld.text(m => m.name, {
                            validators: [required],
                            editor: {
                                type: "text"
                            }
                        })}

                        {bld.singleSelector(m => m.expireTime, {
                            validators: [],
                            editor: {
                                itemsSource: staticItemsSource(
                                    ["1 {hour}", TimeSpan.fromHours(1)],
                                    ["1 {day}", TimeSpan.fromDays(1)],
                                    ["1 {week}", TimeSpan.fromDays(7)],
                                    ["30 {days}", TimeSpan.fromDays(30)],
                                    ["never", undefined]
                                )
                            }
                        })}
                        {bld.editor(m => m.permissions, PermissionEditor, {
                            validators: [],
                            style: ["vertical-label"],
                            editor: {
                            }
                        })}
                    </>}
                />
            </>)
        });
    }

    async showPopupAsync(reqId: string) {

        const uri = fullUri("/access-request/" + reqId);

        const blob = await apiClient.qrCodeAsync(uri);

        const text = formatText("msg-received-access-request", config.current.playStoreUri, uri) as string;

        const shareAsync = () => {

            const title = formatText("msg-invite-title") as string; 

            if ("share" in navigator) {

                navigator.share({
                    title,
                    text,
                    url: uri,
                    files: [new File([blob], "access-request.png", { type: "image/png" })]
                })
            }
            if (isNativeApp()) {
                nativeApp.share({
                    title,
                    text,
                    imageUri: fullUri(apiClient.qrCodeUri(uri))
                })
            }
        }

        const copyAsync = async () => {

            await navigator.clipboard.writeText(text);

            await userInteraction.messageAsync(formatText("msg-text-copied"), "success", 3000);
        }

        const popup = new Popup({
            body: forModel(this, m => <>
                <Image>{blob}</Image>
                <Message type="warning">{formatText("msg-you-have-timeout", "15")}</Message>
                <div>{formatText("msg-else-share-access")}</div>
                <div className="row center">
                    <Action onExecuteAsync={shareAsync} style="text">share</Action>
                    <Action onExecuteAsync={copyAsync} style="text">copy</Action>
                </div>
            </>),
            name: "delegate",
            title: formatText("delegate"),
            actions: [{
                name: "close",
                text: formatText("close") as string,
                executeAsync: async () => true,
            }]
        });


        await popup.showAsync();
    }


    async delegateAsync() {


        if (!await this.editor.commitAsync())
            return;

        const value = this.editor.value;

        let signature;
        const nonce = crypto.randomUUID();

        if (isNativeApp()) {
            signature = await nativeApp.signPayloadAuthAsync(nonce);
            if (!signature)
                return;
        }

        const perm = value.permissions;

        const permissions = [];

        if (perm.salePointIds?.length > 0) {

            for (const scope of perm.scopes) {

                for (const spId of perm.salePointIds)
                    permissions.push(`SalePoint:${scope}:${spId}`);
            }

            const sp = await executeAsync("ListSalePoint", { ids: perm.salePointIds });
            const merchants = distinct(sp.items.map(a => a.merchant.id));

            for (const mcId of merchants)
                permissions.push(`Merchant:read:${mcId}`);

            permissions.push(`SaleCircuit:read:${perm.saleCircuitId}`);

        }
        else if (perm.merchantId) {

            for (const scope of perm.scopes) 
                permissions.push(`Merchant:${scope}:${perm.merchantId}`);

            permissions.push(`SaleCircuit:read:${perm.saleCircuitId}`);
        }
        else {
            for (const scope of perm.scopes) 
                permissions.push(`SaleCircuit:${scope}:${perm.saleCircuitId}`);
        }

        const reqId = await executeAsync("CreateAccessRequest", {
            nonce,
            signature,
            expireTime: new Date(Date.now() + value.expireTime.ticks),
            friendlyName: value.name,
            permissions
        });

        if (!reqId)
            return;

        this.showPopupAsync(reqId);
    }

    editor: ObjectEditor<IDelegateData>;

    static override info = contentInfo({
        name: "delegate-device",
        route: "/devices/delegate",
        icon: <MaterialIcon style="green" name="security_update_good" />,
        features: [Authorize(a => a.businessAccount()
                   .resource("Device", "create"))],
        factory: () => new DelegateDevicePage()
    });
}

registerComponent(DelegateDevicePage, "DelegateDevicePage");